import React, { useEffect, useState } from "react"
import { useRouter } from "next/router"
import { useSelector } from "react-redux"
import { FaPlus, FaChevronRight } from "react-icons/fa"
import { aggregateSlotsByDay } from "../helpers/schedulesHelpers"

const Sidebar = ({ highlight }) => {
  const user = useSelector((state) => state.auth.user)
  const router = useRouter()
  const schedules = useSelector((state) => state.schedules.schedules)
  const scheduleColors = useSelector((state) => state.scheduleColors.scheduleColors)
  const [selectedScheduleId, setSelectedScheduleId] = useState(null)
  const getColorForSchedule = (sch_uid) => scheduleColors[sch_uid] ?? "#FFFFFF"
  const handleScheduleClick = (scheduleId) => {
    setSelectedScheduleId(scheduleId)
    router.push(`/schedules/${scheduleId}`)
  }

  return (
    <div className="p-4 min-w-fit min-h-screen text-gray-800 bg-white border-r border-gray-200 flex flex-col gap-4 overflow-y-auto">
        <div>
          <h3 className="text-sm whitespace-nowrap font-semibold text-gray-500 uppercase tracking-wider mb-2">
            Chargers and Tariffs
          </h3>
          <ul className="space-y-1">
            <SidebarMenuItem
              title="Chargers"
              icon="clock"
              onClick={() => router.push("/chargers/list")}
              selected={highlight === "chargers"}
            />
            <SidebarMenuItem
              title="Sites"
              icon="pin"
              onClick={() => router.push("/sites")}
              selected={highlight === "sites"}
            />
          </ul>
        </div>

        <div>
          <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-2">Tariffs</h3>
          <ul className="space-y-1">
            <SidebarMenuItem
              title="New Tariff"
              icon={<FaPlus size={16} />}
              onClick={() => router.push("/schedules/create-schedule")}
              selected={highlight === "new"}
            />
            {schedules
              .filter((s) => s.deprecated === false)
              .filter((s) => s.client_id === user.client_id)
              .map((schedule) => (
                <ScheduleItem
                  key={schedule.schedule_uid}
                  schedule={schedule}
                  isSelected={selectedScheduleId === schedule.schedule_id}
                  onClick={() => handleScheduleClick(schedule.schedule_uid)}
                  getColorForSchedule={getColorForSchedule}
                />
              ))}
          </ul>
        </div>
    </div>
  )
}

const SidebarMenuItem = ({ title, icon, onClick, selected }) => {
  if (typeof icon == "string") {
    icon = <img src={`/images/${icon}.svg`} alt={`${title} Icon`} className="w-4 h-4" />
  }
  return (
    <li>
      <button
        onClick={onClick}
        className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors duration-150 ease-in-out ${
          selected ? "bg-gray-200 text-gray-900" : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
        }`}
      >
        <span className="text-gray-500 mr-3">{icon}</span>
        <span>{title}</span>
      </button>
    </li>
  )
}

const ScheduleItem = ({ schedule, isSelected, onClick, getColorForSchedule }) => {
  const [isExpanded, setIsExpanded] = useState(false)
  const toggleExpand = (e) => {
    e.stopPropagation()
    setIsExpanded(!isExpanded)
  }

  return (
    <li>
      <button
        onClick={isExpanded ? toggleExpand : () => { onClick(); setIsExpanded(true) }}
        className={`w-full text-left px-3 py-2 rounded-md transition-colors duration-150 ease-in-out ${
          isSelected ? "bg-gray-200" : "hover:bg-gray-100"
        }`}
      >
        <div className="w-full flex items-center justify-between">
          <div className="flex items-center">
            <div
              className="w-3 h-3 rounded-full mr-3"
              style={{ backgroundColor: getColorForSchedule(schedule?.schedule_uid) }}
            />
            <span className="text-sm font-medium text-gray-700 pr-2">{schedule?.schedule_name}</span>
          </div>
          <FaChevronRight
            size={12}
            className={`text-gray-400 transition-transform duration-200 ${isExpanded ? "transform rotate-90" : ""}`}
            onClick={toggleExpand}
          />
        </div>
        {isExpanded && (
          <div className="mt-2 ml-6 space-y-2">
            {aggregateSlotsByDay(schedule.available_slots).map(([dayName, slots]) => (
              <div key={dayName} className="text-xs">
                <div className="font-semibold text-gray-600 mb-1">{dayName}</div>
                <ul>
                  {slots?.map((slot, index) => (
                    <li key={index} className="flex justify-between">
                      <div className="text-gray-500">
                        {slot.startTime} - {slot.endTime}
                      </div>
                      <span className="font-semibold text-gray-700">£{slot.price.toFixed(2)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}
      </button>
    </li>
  )
}

export default Sidebar
